export interface OstCondition {
  name: string;
  quantity: number;
  uom: string | null;
  product: string | undefined;
  notes: string;
  /**
   * Value rendered as the "Order" identifier in the QTO Review sheet.
   * - Non-product rows: monotonically increasing parse-order number (preserves XML order).
   * - Product-linked rows: the resolved `products.item_code`, so reviewers see the catalog
   *   identifier instead of an arbitrary import sequence number.
   *
   * Display-only: this field is mutated post-parse to swap in `item_code` and must
   * not be used to derive identifiers. See `aggregate.ostQtoId` for the stable id.
   */
  order: number | string;
  /**
   * `BidCondition.UID` from the OST XML — the file-internal int identifier used as the
   * FK target by `BidAreaCondition.BidConditionUID` and `BidTakeoff.BidConditionUID`,
   * which guarantees uniqueness across all `<Bid>` elements within one file. Stable
   * across re-exports of the same OST job (assuming the user did not delete and
   * recreate the condition). Source of truth for `qtoId`.
   */
  uid: string;
  /**
   * Which UOM/Quantity slot (1, 2, or 3) on the parent `BidCondition` produced this
   * row. A single condition can expand into up to three rows when more than one slot
   * carries a non-zero quantity, so `(uid, slotIdx)` is the unique key per emitted row.
   */
  slotIdx: 1 | 2 | 3;
}

/**
 * Shape of the per-slot product-id payload embedded as JSON in a `<BidCondition>`'s
 * `Notes` attribute. Each key (`qt1`/`qt2`/`qt3`) maps to an Ediphi product UUID
 * for the corresponding quantity slot. All keys are optional — a single
 * BidCondition typically only fills one or two slots.
 */
export interface EdiphiProduct {
  qt1?: string;
  qt2?: string;
  qt3?: string;
}

/**
 * Shape of the JSON object embedded in `BidCondition.Notes`. The parser
 * locates the `{"ediphi_products": …}` object inside the free-form notes
 * (brace-balanced, so unrelated user-entered braces in the surrounding text
 * are tolerated) and `JSON.parse`s it into this shape. If the payload is
 * missing or malformed, the row still imports without product linking.
 */
export interface EdiphiNotesJson {
  ediphi_products?: EdiphiProduct[];
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Raw OST XML element shapes
 *
 * The parser uses `Element` / `getAttribute` directly, so these interfaces
 * are **documentation-only** — they describe the on-disk attribute set of
 * each element in the OST export. All XML attribute values are strings;
 * JSDoc on each field records the logical type (int, flag, GUID, etc.).
 *
 * Authoritative reference: `excel_addin/docs/ost_file_format.md`. Server
 * counterpart: `api/src/routes/estimates/import_line_items/handle_ost.ts`.
 * ────────────────────────────────────────────────────────────────────────── */

/** OST datetime sentinel for "unset" values (`"1899 12 30 0 0 0"`). */
export const OST_DATETIME_UNSET = "1899 12 30 0 0 0";

/** Numeric `BidCondition.Type` codes. Documentation only. */
export const OST_CONDITION_TYPE = {
  Linear: 0,
  Area: 1,
  Count: 2,
} as const;
export type OstConditionType = (typeof OST_CONDITION_TYPE)[keyof typeof OST_CONDITION_TYPE];

/** `<OST Version="…"/>` — file-format version stamp. */
export interface OstVersionAttrs {
  /** Format version, e.g. `"3.2.0"`. */
  Version: string;
}

/** `<Bid>` — project / bid metadata. Only `JobName` is read by the parser today. */
export interface OstBidAttrs {
  /** int. Internal id; referenced by every child via `BidUID`. */
  UID: string;
  /** int. */
  BidProjectUID?: string;
  /** int. `0` if no parent. */
  ParentBidUID?: string;
  /** FK into `<JobStatuses>`. */
  JobStatusUID?: string;
  /** int FK. */
  EstimatorUID?: string;
  /** int FK. */
  PrManagerUID?: string;
  /** int FK. */
  JobSiteManagerUID?: string;
  /** int. Bid sequence number. */
  BidNo?: string;
  /** int. */
  BidType?: string;
  /** Free-form. */
  JobID?: string;
  /** Human-readable project / library name. **Read by parser.** */
  JobName?: string;
  /** Free-form. */
  Notes?: string;
  /** flag (`"0"` / `"1"`). */
  IsAccepted?: string;
  /** Space-separated `YYYY M D H M S`. `"1899 12 30 0 0 0"` = unset. */
  CreateDateTime?: string;
  /** Space-separated `YYYY M D H M S`. */
  ModDateTime?: string;
  /** Space-separated `YYYY M D H M S`. */
  BidDate?: string;
  /** numeric (string). */
  ScaleFactor1?: string;
  /** numeric (string). */
  ScaleFactor2?: string;
  /** int. */
  ScaleStyle?: string;
  /** numeric. */
  PageWidth?: string;
  /** numeric. */
  PageHeight?: string;
  /** int. */
  HoursPerDay?: string;
  /** int. */
  WeekStartDay?: string;
  /** Stable cross-system identifier, format `"{XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}"`. */
  GUID?: string;
  /** Catch-all for the ~50 other rendering / scheduling flags carried on `<Bid>`. */
  [otherAttribute: string]: string | undefined;
}

/**
 * `<BidCondition>` — one library item ("condition" / "assembly"). The only
 * element the OST parser turns into `OstCondition` rows.
 */
export interface OstBidConditionAttrs {
  /** int. Referenced by `BidAreaCondition.BidConditionUID` and `BidTakeoff.BidConditionUID`. */
  UID: string;
  /** int FK to parent `<Bid>`. */
  BidUID?: string;
  /** int FK to `<BidConditionFolder>` (UniFormat group). */
  BidConditionFolderUID?: string;
  /** int FK to `<BidLayer>`. */
  BidLayerUID?: string;
  /** int FK to `<CdnType>`. */
  CdnTypeUID?: string;
  /** Stable id, `"{XXXXXXXX-…}"`. */
  GUID?: string;
  /** int. Display-only sequence number. */
  RefNo?: string;
  /** Human-readable name. **Read by parser.** */
  Name?: string;
  /**
   * Free-form notes. **The parser extracts the first `{ … }` JSON object from
   * this field and reads `ediphi_products[i].qt{1,2,3}` for product linking.**
   * The remaining text (after the JSON is stripped) is what flows into
   * `OstCondition.notes` (truncated to 500 chars).
   */
  Notes?: string;
  /** int. Geometry type — see {@link OST_CONDITION_TYPE}. */
  Type?: string;
  /** int. */
  Shape?: string;
  /** int. Drawing pattern code. */
  Pattern?: string;
  /** int (packed RGB). */
  ColorLine?: string;
  /** int (packed RGB). */
  ColorFill?: string;
  /** numeric. */
  Width?: string;
  /** numeric. */
  Height?: string;
  /** numeric. */
  Spacing?: string;
  /** numeric. */
  Thickness?: string;
  /** numeric. */
  Rise?: string;
  /** numeric. */
  Run?: string;
  /** numeric. */
  Depth?: string;
  /** UOM code for slot 1; `"-1"` = unused. **Read by parser** (resolved via {@link UOM_MAP}). */
  UOM1?: string;
  /** UOM code for slot 2; `"-1"` = unused. **Read by parser**. */
  UOM2?: string;
  /** UOM code for slot 3; `"-1"` = unused. **Read by parser**. */
  UOM3?: string;
  /** numeric. Per-condition default quantity (parser uses `BidAreaCondition.Quantity1` instead). */
  Quantity1?: string;
  /** numeric. */
  Quantity2?: string;
  /** numeric. */
  Quantity3?: string;
  /** numeric. Material cost (unused by add-in). */
  MatAmount?: string;
  /** numeric. Labor cost (unused by add-in). */
  LabAmount?: string;
  /** numeric. Subcontractor cost (unused by add-in). */
  SubAmount?: string;
  /** flag. */
  IsTemplate?: string;
  /** flag. */
  ShowTakeoff?: string;
  /** flag. */
  DisplayName?: string;
  /** Catch-all for the ~30 other display / rounding / external-id attributes. */
  [otherAttribute: string]: string | undefined;
}

/**
 * `<BidAreaCondition>` — per-area quantity rollup for one
 * `BidCondition` × `BidArea` (or × `BidTypArea`) bucket.
 */
export interface OstBidAreaConditionAttrs {
  /** int. Internal id. */
  UID: string;
  /** int FK to `BidCondition.UID`. */
  BidConditionUID: string;
  /** int FK to `BidArea.UID`; `"0"` = whole-bid bucket. */
  AreaUID?: string;
  /** int FK to `BidTypArea.UID`; `"0"` if not a typical area. */
  TypAreaUID?: string;
  /** numeric. **Summed across all rows by parser** to produce slot-1 quantity. */
  Quantity1?: string;
  /** numeric. **Summed across all rows by parser** to produce slot-2 quantity. */
  Quantity2?: string;
  /** numeric. **Summed across all rows by parser** to produce slot-3 quantity. */
  Quantity3?: string;
}

/** `<BidConditionFolder>` — UniFormat-style grouping folder for `BidCondition`s. */
export interface OstBidConditionFolderAttrs {
  /** int. Identity. */
  UID: string;
  /** int FK to parent `<Bid>`. */
  BidUID?: string;
  /** int FK to parent folder; `"0"` = root-level. */
  ParentUID?: string;
  /** Display label (e.g. `"C2010 - Wall Finishes"`). */
  Name?: string;
  /** Free-form. */
  Description?: string;
  /** flag. UI state at export time. */
  ExpandState?: string;
}

/** `<BidLayer>` — one of `Default` / `Annotation` / `Image` / `Comments`. */
export interface OstBidLayerAttrs {
  /** int. Identity. */
  UID: string;
  /** int FK to `<Bid>`. */
  BidUID?: string;
  /** flag. */
  IsTemplate?: string;
  /** Display label. */
  Name?: string;
  /** flag. */
  Show?: string;
  /** flag. */
  IsLocked?: string;
  /** int. Z-order. */
  Sequence?: string;
}

/** `<BidSetting>` — per-bid render / STS settings. */
export interface OstBidSettingAttrs {
  /** int. Identity. */
  UID: string;
  /** int FK to `<Bid>`. */
  BidUID?: string;
  /** int FK to currently selected `<BidPage>`. */
  BidPageSelectedUID?: string;
  /** STS sync GUID. */
  STSGUID?: string;
  /** STS server name (often empty). */
  STSServerName?: string;
  /** STS client name (often empty). */
  STSClientName?: string;
}

/** `<BidPage>` — a drawing page (sheet). The add-in does not parse these. */
export interface OstBidPageAttrs {
  /** int. Identity. */
  UID: string;
  /** int FK to `<Bid>`. */
  BidUID?: string;
  /** int FK to `<BidPageFolder>`. */
  BidPageFolderUID?: string;
  /** Display label (e.g. `"Page 1"`). */
  Name?: string;
  /** Path or empty / `"NULL"`. */
  ImagePath?: string;
  /** Path or empty / `"NULL"`. */
  OverlayImagePath?: string;
  /** numeric. Sheet width in inches (typical: `"42"`). */
  Width?: string;
  /** numeric. Sheet height in inches (typical: `"30"`). */
  Height?: string;
  /** int. */
  ScaleStyle?: string;
  /** numeric. */
  ScaleFactor1?: string;
  /** numeric. */
  ScaleFactor2?: string;
  /** numeric. */
  Scale?: string;
  /** numeric. */
  ZoomFac?: string;
  /** numeric. */
  CurrentX?: string;
  /** numeric. */
  CurrentY?: string;
  /** flag. */
  FlipX?: string;
  /** flag. */
  FlipY?: string;
  /** numeric. */
  Rotation?: string;
  /** int. Page order. */
  Sequence?: string;
  /** Stable id. */
  GUID?: string;
  /** Catch-all for the ~20 other digitizer / OCR / overlay attributes. */
  [otherAttribute: string]: string | undefined;
}

/** `<BidTakeoff>` — a placed measurement on a page. Not parsed by the add-in. */
export interface OstBidTakeoffAttrs {
  /** int. Identity. */
  UID: string;
  /** int FK to `<Bid>`. */
  BidUID?: string;
  /** int FK to `<BidCondition>`. */
  BidConditionUID?: string;
  /** int FK to `<BidZone>`; `"0"` = none. */
  BidZoneUID?: string;
  /** int FK to `<BidPage>`. */
  BidPageUID?: string;
  /** int FK to `<BidArea>`. */
  BidAreaUID?: string;
  /** int FK to `<BidTypArea>`. */
  BidTypAreaUID?: string;
  /** int FK to parent takeoff; `"0"` = root. */
  ParentUID?: string;
  /** int. Display number. */
  No?: string;
  /** numeric. Computed quantity for this single takeoff. */
  Quantity?: string;
  /** numeric. */
  Count?: string;
  /** numeric. */
  Rotation?: string;
  /**
   * Polyline coordinates: `x1;y1;x2;y2;…` separated by `;` and terminated
   * by a literal `\n` (`&#xa;` in XML).
   */
  Position?: string;
  /** Stable id. */
  GUID?: string;
  /** Catch-all for the ~25 typography / grid / typical-group attributes. */
  [otherAttribute: string]: string | undefined;
}

/** `<CdnType>` — cost-code category lookup (referenced by `BidCondition.CdnTypeUID`). */
export interface OstCdnTypeAttrs {
  /** int. Identity. */
  UID: string;
  /** Display label (e.g. `"0420 - Masonry"`). */
  Name?: string;
  /** flag. UI state. */
  ExpandState?: string;
}

/**
 * `<JobStatuse>` — job-status lookup (referenced by `Bid.JobStatusUID`).
 *
 * **Spelling note:** the singular tag is `JobStatuse`, not `JobStatus`. This
 * is the on-disk schema and must not be "corrected" in the parser.
 */
export interface OstJobStatuseAttrs {
  /** int. Identity. */
  UID: string;
  /** flag. */
  Locked?: string;
  /** Display label. */
  Name?: string;
  /** int. Sort order. */
  Sequence?: string;
}

/**
 * OnScreen Takeoff numeric UOM codes → Ediphi UOM strings. These come from the
 * UOM1/UOM2/UOM3 attributes on each `<BidCondition>` element. `-1` means the slot
 * is unused. The mapping is mirrored on the server in
 * `api/src/routes/estimates/import_line_items/handle_ost.ts`.
 */
export const UOM_MAP: Record<string, string | null> = {
  "-1": null,
  "0": "ea",
  "1": "in",
  "2": "lf",
  "3": "ly",
  "4": "SqIn",
  "5": "sf",
  "6": "sy",
  "7": "sq",
  "8": "cf",
  "9": "cy",
};
